<template>
  <div class="withdraw-modal">

    <div v-if="!selectedCoin" class="withdraw-loading">
      {{ $t('withdraw.loading') }}
    </div>

    <div v-else>
      <!-- SELECTS -->
      <div class="withdraw-selects">
        <div class="select-card">
          <span class="label">{{ $t('withdraw.currency') }}</span>

          <div class="custom-select" @click="toggleCoinDropdown">
            <span>{{ selectedCoin || $t('withdraw.selectCurrency') }}</span>
            <i class="arrow">▾</i>
          </div>

          <div class="dropdown" v-if="coinDropdownOpen">
            <div
              class="option"
              v-for="coin in coins"
              :key="coin.symbol"
              @click="selectCoin(coin.symbol)"
            >
              {{ coin.symbol }}
            </div>
          </div>
        </div>

        <div class="select-card">
          <span class="label">{{ $t('withdraw.network') }}</span>

          <div class="custom-select" @click="toggleNetworkDropdown">
            <span>
              {{
                selectedNetworkConfig
                  ? (selectedNetworkConfig.network === 'Bitcoin'
                      ? $t('withdraw.native')
                      : selectedNetworkConfig.network)
                  : $t('withdraw.selectNetwork')
              }}
            </span>
            <i class="arrow">▾</i>
          </div>

          <div class="dropdown" v-if="networkDropdownOpen">
            <div
              class="option"
              v-for="net in networks"
              :key="net.networkName"
              :class="{ active: net.networkName === selectedNetwork }"
              @click="selectNetwork(net.networkName)"
            >
              {{ net.network === 'Bitcoin' ? $t('withdraw.native') : net.network }}
            </div>
          </div>
        </div>
      </div>

      <!-- BODY -->
      <div class="withdraw-body">
        <div class="input-box">
          <label>{{ $t('withdraw.amount') }}</label>
          <input
            v-model.number="amount"
            type="number"
            :min="withdrawMin || 0"
            :placeholder="$t('withdraw.enterAmount')"
          />
        </div>

        <div class="input-box">
          <label>{{ $t('withdraw.address') }}</label>
          <input
            v-model.trim="address"
            type="text"
            :placeholder="$t('withdraw.enterAddress')"
          />
        </div>

        <p class="info" v-if="withdrawMin">
          {{ $t('withdraw.minWithdraw', { amount: withdrawMin, coin: selectedCoin }) }}
        </p>

        <p class="warning" v-if="selectedNetwork">
          {{ $t('withdraw.networkWarning', { coin: selectedCoin, network: networkLabel }) }}
        </p>

        <button
          class="withdraw-btn"
          :disabled="loading || !amount || !address"
          @click="submitWithdraw"
        >
          {{ loading ? $t('withdraw.processing') : $t('withdraw.submit') }}
        </button>
      </div>
    </div>

  </div>
</template>



<script>
import { mapActions, mapGetters } from 'vuex'
import axios from '@/plugins/axios'
export default {
  name: 'CashierCryptoWithdraw',

  data() {
    return {
      selectedCoin: null,
      selectedNetwork: null,
      amount: null,
      address: '',
      withdrawMin: null,
      coinDropdownOpen: false,
      networkDropdownOpen: false,
      loading: false
    }
  },

computed: {
  ...mapGetters('crypto', ['coins', 'getNetworks']),
  ...mapGetters(['authUser']),
  
  networks() {
    if (!this.selectedCoin) return []
    return this.getNetworks(this.selectedCoin) || []
  },

  networkLabel() {
    const net = this.networks.find(
      n => n.networkName === this.selectedNetwork
    )
    return net
      ? (net.network === 'Bitcoin' ? this.$t('withdraw.native') : net.network)
      : ''
  },

  selectedNetworkConfig() {
    return this.networks.find(
      n => n.networkName === this.selectedNetwork
    )
  }
},


  async created() {
    await this.fetchCryptoOptions()

    if (!this.coins.length) return

    this.selectedCoin = this.coins[0].symbol
    const nets = this.getNetworks(this.selectedCoin)

    if (nets.length) {
      this.selectedNetwork = nets[0].networkName
      this.withdrawMin = nets[0].withdrawMin || null
    }
  },

  watch: {
    selectedNetwork(val) {
      const net = this.networks.find(n => n.networkName === val)
      this.withdrawMin = net?.withdrawMin || null
    }
  },

  methods: {
    ...mapActions('crypto', ['fetchCryptoOptions']),
    ...mapActions(['notificationShow']),

    toggleCoinDropdown() {
      this.coinDropdownOpen = !this.coinDropdownOpen
    },

    toggleNetworkDropdown() {
      if (!this.networks.length) return
      this.networkDropdownOpen = !this.networkDropdownOpen
    },

    selectCoin(symbol) {
      if (this.selectedCoin === symbol) return
      this.selectedCoin = symbol
      this.coinDropdownOpen = false
      this.amount = null
      this.address = ''

      const nets = this.getNetworks(symbol)
      this.selectedNetwork = nets[0]?.networkName || null
      this.withdrawMin = nets[0]?.withdrawMin || null
    },

    selectNetwork(networkName) {
      if (this.selectedNetwork === networkName) return
      this.selectedNetwork = networkName
      this.networkDropdownOpen = false
      this.amount = null
      this.address = ''
    },

    async submitWithdraw() {
      if (this.loading) return

      if (!this.authUser?.user?._id) {
        return this.notificationShow({
          type: 'error',
          message: this.$t('withdraw.errors.login')
        })
      }

      this.loading = true

      try {
        await axios.post('/cryptowithdraw/withdraw', {
  userId: this.authUser.user._id,
  amount: this.amount,
  address: this.address,
  coin: this.selectedCoin,
  network: this.selectedNetwork
})

        this.notificationShow({
          type: 'success',
          message: this.$t('withdraw.success')
        })

        this.$emit('close')
      } catch (err) {
  console.error('[WITHDRAW ERROR]', err)

  const message =
    err.response?.data?.message ||
    err.response?.data?.error ||
    err.message ||
    this.$t('withdraw.errors.generic')

  this.notificationShow({
    type: 'error',
    message
  })
} finally {
        this.loading = false
      }
    }
  }
}
</script>







 <style scoped>
 
 /* ================= ROOT ================= */
.withdraw-modal {
   
  border-radius: 16px;
   margin-top: 25px;
 
  color: #fff;
  font-family: "Rubik", sans-serif;
}

/* ================= LOADING ================= */
.withdraw-loading {
  text-align: center;
  padding: 40px 0;
  font-size: 14px;
  color: #9bb0c5;
}

/* ================= SELECTS ================= */
.withdraw-selects {
     display: grid;
    grid-template-columns: 1fr 10fr;
  gap: 14px;
  margin-bottom: 20px;
}

/* ================= LABEL ================= */
.label {
  font-size: 11px;
  font-weight: 600;
  color: #8fa3b8;
  margin-bottom: 6px;
  display: block;
}

/* ================= SELECT CARD (CURRENCY) ================= */
.select-card {
  position: relative;
}

.custom-select {
  height: 44px;
  padding: 0 14px;
  background: var(--background1);
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all .2s ease;
}

.custom-select span {
  font-size: 14px;
  font-weight: 600;
}

 

.custom-select .arrow {
  font-size: 12px;
  opacity: .6;
}

/* ================= DROPDOWN ================= */
.dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  background: var(--background1);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.06);
  box-shadow: 0 20px 40px rgba(0,0,0,.6);
  max-height: 180px;
  overflow-y: auto;
  z-index: 20;
}

.dropdown::-webkit-scrollbar {
  width: 6px;
}

.dropdown::-webkit-scrollbar-thumb {
  background: rgba(240,195,109,.6);
  border-radius: 6px;
}

.dropdown .option {
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s ease;
}
 

/* ================= NETWORK SELECT ================= */
.select-box select {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  background: var(--background1);
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.06);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.select-box select:focus {
  outline: none;
  
}

/* ================= BODY ================= */
.withdraw-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ================= INPUT BOX ================= */
.input-box {
  display: flex;
  flex-direction: column;
}

.input-box label {
  font-size: 11px;
  font-weight: 600;
  color: #8fa3b8;
  margin-bottom: 6px;
}

.input-box input {
  height: 44px;
  padding: 0 14px;
  background: var(--background1);
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.06);
  color: #fff;
  font-size: 14px;
  transition: all .2s ease;
}

.input-box input::placeholder {
  color: rgba(255,255,255,.35);
}

 

/* ================= INFO / WARNING ================= */
.info {
  font-size: 12px;
  color: #9bb0c5;
}

.warning {
  font-size: 12px;
  color: #f0c36d;
  background: rgba(240,195,109,.08);
  border-radius: 8px;
  padding: 10px 12px;
}

/* ================= BUTTON ================= */
.withdraw-btn {
  margin-top: 8px;
  height: 48px;
  border-radius: 14px;
  border: none;
  background: var(--buttonyellow);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: var(--buttonyellowbox);
  transition: all .2s ease;
}

.withdraw-btn:hover {
  transform: translateY(-1px);
 
}

.withdraw-btn:active {
  transform: translateY(0);
}

/* ================= RESPONSIVE ================= */
@media (max-width: 560px) {
   

  .withdraw-modal {
    padding: 18px;
  }
}


</style>
